"use client";

import { useState, useEffect, useCallback } from "react";

export interface NotifyOptions {
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  data?: Record<string, any>;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission | "default">("default");
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasSupport = "Notification" in window && "serviceWorker" in navigator;
    setSupported(hasSupport);
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const request = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return "denied" as NotificationPermission;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const notify = useCallback(async (title: string, options: NotifyOptions = {}) => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body: options.body,
        icon: options.icon || "/k-logo.png",
        badge: options.badge || "/k-logo.png",
        tag: options.tag,
        requireInteraction: options.requireInteraction,
        data: options.data,
      });
    } catch (e) {
      // Fallback to legacy Notification API (mobile Safari may not have service worker notifications)
      try {
        new Notification(title, {
          body: options.body,
          icon: options.icon || "/k-logo.png",
        });
      } catch {}
    }
  }, []);

  const registerSync = useCallback(async (tag: string = "sync-messages") => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return false;
    try {
      const registration = await navigator.serviceWorker.ready;
      if ("sync" in registration) {
        await (registration as any).sync.register(tag);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, []);

  // Server push subscription placeholder: wire up when VAPID keys are configured.
  const subscribe = useCallback(async () => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
    try {
      const registration = await navigator.serviceWorker.ready;
      const vapid = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
      if (!vapid) return null;
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
      // Ideally POST /api/notifications/subscribe with sub.toJSON()
      return sub;
    } catch (e) {
      return null;
    }
  }, []);

  return { supported, permission, request, notify, registerSync, subscribe };
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
